import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useAuthStore } from '../store/auth-store';
import type { UserInfo, LoginResponse, RefreshTokenResponse } from '../api/JsdhNotifikaceService/generated';

interface AuthContextType {
    user: UserInfo | null;
    token: string | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isAdmin: boolean;
    login: (response: LoginResponse) => void;
    logout: () => void;
    updateUser: (user: UserInfo) => void;
    updateTokens: (response: RefreshTokenResponse) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Subscribe to individual state values (not getters) for proper reactivity
    const user = useAuthStore((state) => state.user);
    const accessToken = useAuthStore((state) => state.accessToken);
    const refreshToken = useAuthStore((state) => state.refreshToken);
    const isLoading = useAuthStore((state) => state.isLoading);
    const login = useAuthStore((state) => state.login);
    const logout = useAuthStore((state) => state.logout);
    const updateUser = useAuthStore((state) => state.updateUser);
    const updateTokens = useAuthStore((state) => state.updateTokens);

    // Compute derived values in React (not as Zustand getters)
    const isAuthenticated = !!accessToken && !!user;
    const isAdmin = user?.role === 'ROLE_ADMIN' || user?.role === 'ADMIN'
        || user?.role === 'ROLE_SUPER_ADMIN' || user?.role === 'SUPER_ADMIN';

    const value = useMemo<AuthContextType>(() => ({
        user,
        token: accessToken,
        accessToken,
        refreshToken,
        isAuthenticated,
        isLoading,
        isAdmin,
        login,
        logout,
        updateUser,
        updateTokens,
    }), [user, accessToken, refreshToken, isAuthenticated, isLoading, isAdmin, login, logout, updateUser, updateTokens]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
